import React, { useState, useEffect } from "react";
import {
  BadgeCheck,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  FileText,
  Eye,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import BackButton from "../../components/BackButton";
import { Spinner, Alert } from "../../components/ui";

const STATUS_CONFIG = {
  SUBMITTED: { label: "Diajukan", color: "bg-amber-50 text-amber-600", icon: Clock },
  UNDER_REVIEW: { label: "Sedang Direview", color: "bg-blue-50 text-blue-600", icon: Clock },
  SKCK_PENDING: { label: "SKCK Belum Terverifikasi", color: "bg-amber-50 text-amber-600", icon: AlertTriangle },
  APPROVED: { label: "Disetujui", color: "bg-success-50 text-success-600", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak", color: "bg-red-50 text-red-600", icon: XCircle },
};

export default function AdminKYC() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ktpImg, setKtpImg] = useState(null);
  const [selfieImg, setSelfieImg] = useState(null);
  const [skckImg, setSkckImg] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const fetchSubmissions = async () => {
    try {
      const { data } = await api.get("/api/admin/kyc/pending");
      setSubmissions(data);
    } catch (err) {
      console.error("Failed to load KYC submissions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchDocument = async (submissionId, docType) => {
    try {
      const response = await api.get(`/api/admin/kyc/${submissionId}/document/${docType}`, {
        responseType: "blob",
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      console.error(`Failed to load ${docType}:`, err);
      return null;
    }
  };

  const handleSelect = async (sub) => {
    setSelected(sub);
    setKtpImg(null);
    setSelfieImg(null);
    setSkckImg(null);
    setLoadingDocs(true);
    const [ktpUrl, selfieUrl, skckUrl] = await Promise.all([
      fetchDocument(sub.id, "ktp"),
      fetchDocument(sub.id, "selfie"),
      sub.has_skck ? fetchDocument(sub.id, "skck") : Promise.resolve(null),
    ]);
    setKtpImg(ktpUrl);
    setSelfieImg(selfieUrl);
    setSkckImg(skckUrl);
    setLoadingDocs(false);
  };

  const handleReview = async (action) => {
    if (!selected) return;
    setReviewing(true);
    setMsg(null);
    try {
      await api.post(`/api/admin/kyc/${selected.id}/review`, {
        action,
        note: reviewNote || null,
      });
      setMsg({ type: "success", text: `KYC berhasil ${action === "APPROVE" ? "disetujui" : "ditolak"}.` });
      setSelected(null);
      setReviewNote("");
      setKtpImg(null);
      setSelfieImg(null);
      fetchSubmissions();
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err.response?.data?.detail) });
    } finally {
      setReviewing(false);
    }
  };

  if (loading) return <Spinner />;

  if (selected) {
    return (
      <div className="animate-fade-up grid place-items-center">
        <div className="container max-w-lg space-y-6">
          <button
          onClick={() => { setSelected(null); setKtpImg(null); setSelfieImg(null); setSkckImg(null); }}
          className="flex items-center gap-2 text-brand-500 hover:text-brand-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Kembali
        </button>
          <div>
            <h1 className="text-brand-800">Review KYC</h1>
            <p className="text-brand-400 mt-1">Detail verifikasi identitas pekerja</p>
          </div>

          {msg && <Alert kind={msg.type}>{msg.text}</Alert>}

          {/* Worker Info */}
          <div className="card p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-brand-100 grid place-items-center text-lg font-bold text-brand-700">
                {(selected.worker_display_name || "W")[0].toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-brand-800">{selected.worker_display_name || "Pekerja"}</p>
                <span className="chip bg-brand-50 text-brand-600 text-xs">{selected.worker_tier || "LITE"}</span>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="card p-6 space-y-4">
            <h2 className="text-brand-800 text-lg">Dokumen</h2>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-brand-600">
                <FileText className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-brand-400">Nama Lengkap (KTP)</p>
                  <p className="font-medium">{selected.full_legal_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-brand-600">
                <FileText className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-brand-400">Nomor KTP</p>
                  <p className="font-medium">{selected.ktp_number}</p>
                </div>
              </div>

              <div className="border-t border-brand-50 pt-3">
                <p className="text-sm text-brand-400 mb-2">Foto KTP</p>
                {loadingDocs ? (
                  <div className="flex items-center justify-center p-6 bg-brand-50 rounded-xl">
                    <Spinner label="Memuat dokumen..." />
                  </div>
                ) : ktpImg ? (
                  <div className="rounded-xl overflow-hidden bg-brand-50 border border-brand-100">
                    <img
                      src={ktpImg}
                      alt="Foto KTP"
                      className="w-full h-auto object-contain max-h-64"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-6 bg-brand-50 rounded-xl text-brand-400 text-sm">
                    <FileText className="h-8 w-8 mr-2" /> Dokumen tidak tersedia
                  </div>
                )}
              </div>

              <div className="border-t border-brand-50 pt-3">
                <p className="text-sm text-brand-400 mb-2">Foto Selfie + KTP</p>
                {loadingDocs ? (
                  <div className="flex items-center justify-center p-6 bg-brand-50 rounded-xl">
                    <Spinner label="Memuat dokumen..." />
                  </div>
                ) : selfieImg ? (
                  <div className="rounded-xl overflow-hidden bg-brand-50 border border-brand-100">
                    <img
                      src={selfieImg}
                      alt="Selfie + KTP"
                      className="w-full h-auto object-contain max-h-64"
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-6 bg-brand-50 rounded-xl text-brand-400 text-sm">
                    <FileText className="h-8 w-8 mr-2" /> Dokumen tidak tersedia
                  </div>
                )}
              </div>

              {selected.has_skck && (
                <div className="border-t border-brand-50 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-brand-400">Foto SKCK</p>
                    {selected.skck_expiry && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${new Date(selected.skck_expiry) < new Date() ? "bg-red-100 text-red-600" : "bg-success-50 text-success-600"}`}>
                        {new Date(selected.skck_expiry) < new Date() ? "Kadaluarsa" : `Berlaku s/d ${new Date(selected.skck_expiry).toLocaleDateString("id-ID")}`}
                      </span>
                    )}
                  </div>
                  {loadingDocs ? (
                    <div className="flex items-center justify-center p-6 bg-brand-50 rounded-xl">
                      <Spinner label="Memuat dokumen..." />
                    </div>
                  ) : skckImg ? (
                    <div className="rounded-xl overflow-hidden bg-brand-50 border border-brand-100">
                      <img
                        src={skckImg}
                        alt="SKCK"
                        className="w-full h-auto object-contain max-h-64"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-6 bg-brand-50 rounded-xl text-brand-400 text-sm">
                      <FileText className="h-8 w-8 mr-2" /> Dokumen tidak tersedia
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Review Action */}
          <div className="card p-6 space-y-4">
            <h2 className="text-brand-800 text-lg">Keputusan Review</h2>

            {/* SKCK Status Warning */}
            {(!selected.has_skck || (selected.skck_expiry && new Date(selected.skck_expiry) < new Date())) && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-700 font-medium">
                  {!selected.has_skck ? "⚠ SKCK belum diunggah oleh pekerja" : "⚠ SKCK sudah kadaluarsa"}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  KYC tidak bisa disetujui penuh. Pilih "Tandai SKCK Pending" agar status = "SKCK Belum Terverifikasi".
                </p>
              </div>
            )}

            <div>
              <label className="text-sm text-brand-500">Catatan (opsional)</label>
              <textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Catatan untuk pekerja..."
                rows={3}
                className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none resize-none"
                data-testid="kyc-review-note"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => handleReview("REJECT")}
                disabled={reviewing}
                className="btn-ghost flex-1 border-red-200 text-red-600 hover:bg-red-50"
                data-testid="kyc-reject-btn"
              >
                <XCircle className="h-4 w-4" /> Tolak
              </button>

              {(!selected.has_skck || (selected.skck_expiry && new Date(selected.skck_expiry) < new Date())) ? (
                <button
                  onClick={() => handleReview("SKCK_PENDING")}
                  disabled={reviewing}
                  className="btn-warning flex-1"
                  data-testid="kyc-skck-pending-btn"
                >
                  <AlertTriangle className="h-4 w-4" /> Tandai SKCK Pending
                </button>
              ) : (
                <button
                  onClick={() => handleReview("APPROVE")}
                  disabled={reviewing}
                  className="btn-success flex-1"
                  data-testid="kyc-approve-btn"
                >
                  <CheckCircle2 className="h-4 w-4" /> Setujui
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up grid place-items-center">
      <div className="container space-y-6">
        <BackButton to="/admin" />
        <div>
          <h1 className="text-brand-800">Review KYC</h1>
          <p className="text-brand-400 mt-1">Verifikasi identitas pekerja</p>
        </div>

        {msg && <Alert kind={msg.type}>{msg.text}</Alert>}

        {submissions.length === 0 ? (
          <div className="card p-10 text-center">
            <BadgeCheck className="h-12 w-12 text-brand-200 mx-auto" />
            <p className="font-bold text-brand-800 mt-4">Tidak ada verifikasi menunggu</p>
            <p className="text-brand-400 mt-1">Semua pengajuan KYC sudah direview.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {submissions.map((sub) => {
              const status = STATUS_CONFIG[sub.status] || STATUS_CONFIG.SUBMITTED;
              const StatusIcon = status.icon;
              return (
                <button
                  key={sub.id}
                  onClick={() => handleSelect(sub)}
                  className="card p-4 hover:border-brand-200 transition text-left w-full"
                  data-testid={`kyc-card-${sub.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-brand-100 grid place-items-center text-sm font-bold text-brand-700">
                        {(sub.worker_display_name || "W")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold text-brand-800">{sub.worker_display_name || "Pekerja"}</p>
                        <p className="text-sm text-brand-500">{sub.full_legal_name}</p>
                        <p className="text-xs text-brand-400 mt-0.5">
                          Diajukan: {new Date(sub.submitted_at).toLocaleDateString("id-ID", {
                            day: "numeric", month: "long", year: "numeric",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`chip ${status.color}`}>
                        <StatusIcon className="h-3 w-3" /> {status.label}
                      </span>
                      <Eye className="h-4 w-4 text-brand-400" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
